import { Box, TextField, Typography, Divider, Chip, IconButton, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DesignAppMode } from '@/enums/design-app-modes.enum';
import { I18N_KEYS } from '@/language/keys';

type Node = {
  id: string;
  data: {
    name: string;
    description: string;
  };
};

type MenuAction = { label: React.ReactNode; onClick: () => void; disabled?: boolean; id: string };

export type DescriptionRenderProps = {
  description: string;
  inEditMode: boolean;
  editingDescription: boolean;
  setEditingDescription: (editing: boolean) => void;
  onDescriptionChange: (description: string) => void;
};

type DesignAppInputHeaderProps = {
  inputId: string;
  updateNodeData: (id: string, data: any) => void;
  role: string;
  inputColor: string;
  nodes: Node[];
  setIsEditingInputMetadata: (isEditing: boolean) => void;
  mode: DesignAppMode;
  actions?: MenuAction[];
  renderDescription?: (props: DescriptionRenderProps) => React.ReactNode;
};

function DesignAppInputHeader({
  inputId,
  updateNodeData,
  role,
  inputColor,
  nodes,
  setIsEditingInputMetadata,
  mode,
  actions,
  renderDescription,
}: DesignAppInputHeaderProps) {
  const [editingInputId, setEditingInputId] = useState<string | null>(null);
  const [editingDescription, setEditingDescription] = useState(false);
  const anchorEl = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  const inEditMode = role === 'editor' && mode === DesignAppMode.Editing;

  useEffect(() => {
    if (editingInputId || editingDescription) {
      setIsEditingInputMetadata(true);
    } else {
      setIsEditingInputMetadata(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingInputId, editingDescription]);

  const handleInputNameChange = (newName: string) => {
    if (inEditMode) {
      updateNodeData(inputId, { name: newName });
    }
  };
  const handleInputDescriptionChange = (newDescription: string) => {
    if (inEditMode) {
      updateNodeData(inputId, { description: newDescription });
    }
  };

  const currentNode = nodes.find((node) => node.id === inputId);

  if (!currentNode) {
    return null;
  }

  const descriptionProps: DescriptionRenderProps = {
    description: currentNode.data.description,
    inEditMode,
    editingDescription,
    setEditingDescription,
    onDescriptionChange: handleInputDescriptionChange,
  };

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        mb: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Divider sx={{ ml: -2, flexGrow: 1 }} textAlign="left">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {inEditMode && (
              <Box sx={{ width: '12px', display: 'flex', alignItems: 'center' }}>
                <img src="/icons/dots.svg" width="12px" style={{ opacity: 0.8, cursor: 'grab' }} draggable={false} />
              </Box>
            )}
            {editingInputId === inputId ? (
              <TextField
                autoFocus
                fullWidth
                size="small"
                defaultValue={currentNode.data.name}
                onFocus={(e) => {
                  e.target.select();
                }}
                onBlur={(e) => {
                  handleInputNameChange(e.target.value);
                  setEditingInputId(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && 'value' in e.target && typeof e.target.value === 'string') {
                    handleInputNameChange(e.target.value);
                    setEditingInputId(null);
                  }
                  if (e.key === 'Escape') {
                    setEditingInputId(null);
                  }
                }}
                sx={{
                  '& .MuiInput-root': {
                    fontSize: '0.8125rem',
                    '&:before, &:after': {
                      borderBottom: `1px solid ${inputColor}`,
                    },
                  },
                }}
                variant="standard"
              />
            ) : (
              <Chip
                size="small"
                label={currentNode.data.name}
                sx={{
                  background: inputColor,
                  cursor: inEditMode ? 'text' : 'default',
                  '&:hover': {
                    background: inEditMode ? inputColor : 'transparent',
                  },
                }}
                onClick={() => inEditMode && setEditingInputId(inputId)}
              />
            )}
          </Box>
        </Divider>
        {inEditMode ? (
          <Box>
            <IconButton size="small" ref={anchorEl} onClick={() => setOpen(true)}>
              <MoreVertIcon fontSize="inherit" />
            </IconButton>
            <Menu
              anchorEl={anchorEl.current}
              open={open}
              onClose={() => setOpen(false)}
              MenuListProps={{
                disablePadding: true,
              }}
            >
              {actions?.map((action) => (
                <MenuItem key={action.id} disabled={action.disabled} onClick={action.onClick}>
                  {action.label}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        ) : null}
      </Box>
      <Box id="design-appinput-description" sx={{ mt: 1 }}>
        {renderDescription ? renderDescription(descriptionProps) : <DefaultDescription {...descriptionProps} />}
      </Box>
    </Box>
  );
}

function DescriptionBase({
  displayText,
  editDefaultValue,
  skipEmptySave,
  inEditMode,
  editingDescription,
  setEditingDescription,
  onDescriptionChange,
}: {
  displayText: string;
  editDefaultValue: string;
  skipEmptySave?: boolean;
  inEditMode: boolean;
  editingDescription: boolean;
  setEditingDescription: (editing: boolean) => void;
  onDescriptionChange: (description: string) => void;
}) {
  const { t: translate } = useTranslation();

  if (editingDescription) {
    return (
      <TextField
        autoFocus
        multiline
        fullWidth
        size="small"
        placeholder={translate(I18N_KEYS.SHARED_DESIGN_APP.INPUTS.HEADER.DESCRIPTION_PLACEHOLDER)}
        defaultValue={editDefaultValue}
        onBlur={(e) => {
          if (!skipEmptySave || e.target.value) {
            onDescriptionChange(e.target.value);
          }
          setEditingDescription(false);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && 'value' in e.target && typeof e.target.value === 'string') {
            onDescriptionChange(e.target.value);
            setEditingDescription(false);
          }
          if (e.key === 'Escape') {
            setEditingDescription(false);
          }
        }}
        variant="standard"
        slotProps={{
          input: {
            sx: {
              fontSize: '0.75rem',
              '& textarea': { maxHeight: 240, overflowY: 'auto !important' },
            },
          },
        }}
      />
    );
  }

  return (
    <Typography
      noWrap
      component="div"
      variant="body-sm-rg"
      sx={{ cursor: inEditMode ? 'text' : 'default' }}
      onClick={() => inEditMode && setEditingDescription(true)}
    >
      {displayText}
    </Typography>
  );
}

function DefaultDescription({ description, inEditMode, ...rest }: DescriptionRenderProps) {
  const { t: translate } = useTranslation();
  const displayText =
    description || (inEditMode ? translate(I18N_KEYS.SHARED_DESIGN_APP.INPUTS.HEADER.ADD_DESCRIPTION) : '');

  return <DescriptionBase displayText={displayText} editDefaultValue={description} inEditMode={inEditMode} {...rest} />;
}

export function IteratorDescription({
  description,
  defaultDescription,
  inEditMode,
  ...rest
}: DescriptionRenderProps & { defaultDescription: string }) {
  const { t: translate } = useTranslation();
  const hasCustomDescription = !!description && description !== defaultDescription;
  const displayText = hasCustomDescription
    ? description
    : inEditMode
      ? translate(I18N_KEYS.SHARED_DESIGN_APP.INPUTS.HEADER.ADD_DESCRIPTION)
      : '';

  return (
    <DescriptionBase
      displayText={displayText}
      editDefaultValue={hasCustomDescription ? description : ''}
      skipEmptySave
      inEditMode={inEditMode}
      {...rest}
    />
  );
}

export default DesignAppInputHeader;
